import React, { useState, useEffect } from 'react';
import clsx from 'clsx';
import {ReactCountryFlag} from 'react-country-flag'
import { getCountryName } from '../../../utils/country';
import { IClientBreakdown, IClientMetric } from '../../../api';

interface Pane {
  label: string;
  slug: keyof IClientBreakdown;
}

const panes: Pane[] = [
  { label: 'Browser', slug: 'browser' },
  { label: 'OS', slug: 'os' },
  { label: 'Device', slug: 'device' },
  { label: 'Language', slug: 'language' },
  { label: 'Screen', slug: 'screen' },
];

interface PaneProps {
  items: IClientMetric[];
  maxItems?: number;
  renderLabel?: (item: IClientMetric) => React.ReactNode;
}

export const PaneContent: React.FC<PaneProps> = ({ items, maxItems = 10, renderLabel }) => {
  const total = items.reduce((s, i) => s + i.count, 0);
  const displayed = items.slice(0, maxItems);
  const others = items.slice(maxItems);
  const othersValue = others.reduce((s, i) => s + i.count, 0);
  const othersPercent = total > 0 ? ((othersValue / total) * 100).toFixed(1) : '0.0';

  return (
    <>
      {!items || items.length === 0 ? (
        <div>No data available.</div>
      ) : (
        <div className="d-flex flex-column gap-2">
          {displayed.map((item, i) => {
            const value = item.count;
            const percent = total > 0 ? ((value / total) * 100).toFixed(1) : '0.0';
            return (
              <div key={`country-${i}`} className="d-flex justify-content-between align-items-center">
                <div className="text-truncate fw-bold text-gray-600 me-3">
                  {renderLabel ? renderLabel(item) : item.name || 'Unknown'}
                </div>
                <div className="d-flex align-items-center flex-shrink-0">
                  <span className="fw-bold text-end" style={{ minWidth: '60px' }}>{value}</span>
                  <span className="text-gray-600 text-end" style={{ minWidth: '50px' }}>{percent}%</span>
                </div>
              </div>
            );
          })}
          {othersValue > 0 && (
            <div key="others" className="d-flex justify-content-between align-items-center">
              <div className="fw-bold text-gray-600 me-3">Others {others.length}</div>
              <div className="d-flex align-items-center flex-shrink-0">
                <span className="fw-bold text-end" style={{ minWidth: '60px' }}>{othersValue}</span>
                <span className="text-gray-600 text-end" style={{ minWidth: '50px' }}>{othersPercent}%</span>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  )

};

interface EnvironmentProps {
  values: IClientBreakdown;
  className?: string;
}

/**
 * Environment Card with panes
 */
export const TopEnvironmentsCard: React.FC<EnvironmentProps> = ({ values, className }) => {
  const [pane, setPane] = useState<keyof IClientBreakdown>('browser');
  const [data, setData] = useState<IClientMetric[]>(values[pane]);

  useEffect(() => {
    setData(values[pane]);
  }, [pane, values]);

  return (
    <div className={clsx('card', className)}>
      <div className="card-header d-flex align-items-center justify-content-between">
        <div className="card-title">Environment</div>
        <ul className="nav nav-pills mb-0">
          {panes.map((p) => (
            <li key={p.slug} className="nav-item">
              <button
                className={`nav-link ${pane === p.slug ? 'active' : ''}`}
                onClick={() => setPane(p.slug)}
              >
                {p.label}
              </button>
            </li>
          ))}
        </ul>
      </div>
      <div className="card-body">
        <PaneContent items={data} />
      </div>
    </div>
  );
};

interface CountryProps {
  items: IClientMetric[];
  className?: string;
}

export const TopCountriesCard: React.FC<CountryProps> = ({ items, className }) => {
  return (
    <div className={clsx('card', className)}>
      <div className="card-header d-flex align-items-center justify-content-between">
        <div className="card-title">Top Countries</div>
      </div>
      <div className="card-body">
        <PaneContent
          items={items}
          renderLabel={(item: IClientMetric) => (
            <>
              {item.name ? (
                <>
                  <ReactCountryFlag countryCode={item.name} style={{ fontSize: '1.5em' }} className="me-3"/>
                  <span className="text-gray-600 fw-bold">{getCountryName(item.name, 'en')}</span>
                </>
              ) : (
                <>Unknown</>
              )}
            </>
          )}
        />
      </div>
    </div>
  );
}
